import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Briefcase, Building, Dumbbell, Baby, Building2, ShoppingBag, CircleCheck, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const industries = [{
  id: "offices",
  label: "Offices",
  title: "Corporate & Office Spaces",
  description: "Maintain a pristine professional environment with our comprehensive office cleaning solutions. We ensure your workspace reflects the high standards of your business.",
  protocols: ["Floor scrubbing", "Workstation & IT equipment sanitization", "Boardroom & reception detailing", "Kitchen & washroom deep cleaning"],
  icon: Briefcase,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/office_cleaning_modern_interior.png"
}, {
  id: "strata",
  label: "Strata",
  title: "Strata & Residential Buildings",
  description: "Reliable upkeep for common areas, ensuring a welcoming and hygienic environment for residents and visitors alike.",
  protocols: ["Lobby & lift sanitization", "Corridor vacuuming & mopping", "Bin management & waste removal", "Glass & window cleaning"],
  icon: Building,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/strata_building_exterior_facade.png"
}, {
  id: "gyms",
  label: "Gyms",
  title: "Gyms & Fitness Centres",
  description: "Specialized cleaning to combat bacteria and odors in high-traffic fitness environments, protecting your members' health.",
  protocols: ["Equipment disinfection & wipe-down", "Locker room & shower hygiene", "Clean walls for presentation", "Odor control & air quality management"],
  icon: Dumbbell,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/gym_fitness_center_equipment.png"
}, {
  id: "childcare",
  label: "Childcare",
  title: "Childcare & Early Learning",
  description: "Safe, non-toxic cleaning ensuring a healthy play environment for children. We adhere to strict hygiene standards for peace of mind.",
  protocols: ["Non-toxic, child-safe products", "Toy & surface sanitization", "HEPA filtration for air quality", "Infection control procedures"],
  icon: Baby,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/childcare_center_playroom.png"
}, {
  id: "commercial",
  label: "Commercial",
  title: "Commercial Properties",
  description: "Versatile cleaning solutions for showrooms, warehouses, and mixed-use properties, tailored to your specific operational needs.",
  protocols: ["Floor stripping & sealing", "High-dusting & cobweb removal", "Industrial carpet cleaning", "External pressure washing", "Professional Driveway Sealing", "Carpet Cleaning"],
  icon: Building2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/commercial_property_foyer.png"
}, {
  id: "retail",
  label: "Retail",
  title: "Retail & Showrooms",
  description: "Immaculate presentation to enhance customer experience and brand image. We help you make the perfect first impression.",
  protocols: ["Display & glass polishing", "Fitting room sanitization", "High-traffic floor maintenance", "Dust-free shelving & fixtures"],
  icon: ShoppingBag,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/retail_store_luxury_interior.png"
}];
export default function IndustriesServed() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="industries-served" ref={ref} className={`dark bg-background text-foreground py-20 lg:py-28 relative overflow-hidden transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-12 lg:mb-16 max-w-3xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Industries Served
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl">
            Tailored cleaning protocols designed for the unique needs of every sector.
          </p>
        </div>

        <Tabs defaultValue="offices" className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {}
          <div className="w-full lg:w-5/12 flex flex-col gap-8">
            {}
            <TabsList className="flex flex-row lg:flex-col h-auto w-full justify-start bg-transparent p-0 gap-2 lg:gap-0 lg:space-y-1 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
              {industries.map((industry, i) => <TabsTrigger key={industry.id} value={industry.id} data-index={i} className="w-auto lg:w-full justify-start px-4 py-3 text-sm md:text-base font-medium rounded-full lg:rounded-lg border border-border lg:border-none lg:border-l-2 lg:border-transparent data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-muted-foreground hover:text-foreground transition-all shrink-0 data-[state=active]:shadow-none">
                  <industry.icon className="w-4 h-4 mr-2" />
                  {industry.label}
                </TabsTrigger>)}
            </TabsList>

            {}
            <div className="min-h-[300px]">
              {industries.map((industry, i) => <TabsContent key={industry.id} value={industry.id} data-index={i} className="mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
                        {industry.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {industry.description}
                      </p>
                    </div>

                    <div className="bg-card/50 border border-border/50 rounded-xl p-6 backdrop-blur-sm">
                      <h4 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4">
                        Key Protocols
                      </h4>
                      <ul className="space-y-3">
                        {industry.protocols.map((protocol, idx) => <li key={idx} className="flex items-start gap-3 text-sm md:text-base text-foreground/90">
                            <CircleCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                            <span>{protocol}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="pt-2">
                      <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                        <Link to="/ContactUsPage">
                          Get a Quote for {industry.label}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </TabsContent>)}
            </div>
          </div>

          {}
          <div className="w-full lg:w-7/12 relative min-h-[300px] lg:min-h-[600px] rounded-2xl overflow-hidden shadow-2xl border border-border/20 bg-card">
            {industries.map((industry, i) => <TabsContent key={industry.id} value={industry.id} data-index={i} className="absolute inset-0 w-full h-full mt-0 animate-in fade-in zoom-in-95 duration-700">
                <div className="relative w-full h-full">
                  {}
                  <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105" style={{
                backgroundImage: `url(${industry.image})`
              }} />
                  
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                  
                  {}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-md border border-white/10 text-foreground text-sm font-medium shadow-lg">
                      <industry.icon className="w-4 h-4 text-primary" />
                      <span>Professional {industry.label} Cleaning</span>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </div>
        </Tabs>
      </div>
    </section>;
}